import { pick, has } from 'lodash';
import removeRepeaterItem from 'APP_ROOT/actions/remove-repeater-item';
import updateGivenDataKeys from 'APP_ROOT/actions/update-given-data-keys.js';
import {
  symlinkPathPropertyName,
  symlinksPropertiesPropName,
} from 'APP_ROOT/utils/create-symlink';

/**
 * Pick relevant fields from a repeater item
 * @param {Object} item - repeater item
 * @returns {string[]} field names to pick
 */
const pickFields = item => {
  const fields = ['id'];

  if (has(item, symlinkPathPropertyName)) {
    fields.push(symlinkPathPropertyName);
  }

  if (has(item, symlinksPropertiesPropName)) {
    fields.push(symlinksPropertiesPropName);
    const symlinkProp = item[symlinksPropertiesPropName]?.[0];
    if (symlinkProp) {
      fields.push(symlinkProp);
    }
  }

  return fields;
};

/**
 * Get form data from active or review form
 * @param {Object} state - redux state
 * @returns {Object} data from the active or review form
 */
const getFormData = state => {
  const {
    form: { selectedForm: { isActive, data: formData = {} } = {} } = {},
    reviewForm: { selectedForm: { data: reviewFormData = {} } = {} } = {},
  } = state;

  return isActive ? formData : reviewFormData;
};

/**
 * Get information about the parent repeater
 * @param {Object} data - form data
 * @param {string} parentKey - parent repeater key
 * @param {number} parentIndex - parent item index
 * @returns {Object} parent repeater information
 */
const getParentRepeaterInfo = (data, parentKey, parentIndex) => {
  if (!parentKey) {
    return { parentRepeater: null, symlinkProps: null, symlinkPropValue: null };
  }

  const parentRepeaterData = data[parentKey] || [];
  const parentRepeater = parentRepeaterData[parentIndex] || null;
  const symlinkProps =
    parentRepeater?.[symlinksPropertiesPropName]?.[0] || null;
  const symlinkPropValue = symlinkProps ? parentRepeater[symlinkProps] : null;

  return { parentRepeater, symlinkProps, symlinkPropValue };
};

/**
 * Process an item and adds it to the target array
 * @param {Object} item - item to process
 * @param {Array} targetArray - array to add the processed item
 * @returns {boolean} true if fields were removed from the original item
 */
const processAndAddItem = (item, targetArray) => {
  const pickedFields = pickFields(item);
  const processedItem = pick(item, pickedFields);
  targetArray.push(processedItem);

  return Object.keys(item).length !== pickedFields.length;
};

/**
 * Procesa child repeaters manteniendo solo el primer item
 * @param {Object} item - Item padre que contiene child repeaters
 * @param {Object} data - Datos completos del formulario
 * @returns {Object} Información del child repeater procesado
 */
const processChildRepeaters = (item, data) => {
  if (!item[symlinksPropertiesPropName]) {
    return {
      childRepeaterKey: null,
      newChildRepeaterData: [],
      hasChanges: false,
    };
  }

  const childRepeaterKey = item[symlinksPropertiesPropName][0];
  const childRepeaterData = data[childRepeaterKey] || [];
  const newChildRepeaterData = [];
  let hasChanges = false;

  // Only process the first item of the child repeater
  childRepeaterData.forEach((childItem, childIndex) => {
    if (childIndex === 0) {
      hasChanges =
        processAndAddItem(childItem, newChildRepeaterData) || hasChanges;
    }
  });

  // If there was more than one item, it's also a change
  hasChanges = hasChanges || childRepeaterData.length > 1;

  return { childRepeaterKey, newChildRepeaterData, hasChanges };
};

/**
 * Process items when there is a parent key (filter by symlink)
 * @param {Array} repeaterData - repeater data
 * @param {string} symlinkPropValue - symlink property value to filter
 * @param {Function} dispatch - Redux dispatch function
 * @param {string} repeaterKey - repeater key
 * @returns {Object} processing result
 */
const processWithParentKey = (
  repeaterData,
  symlinkPropValue,
  dispatch,
  repeaterKey
) => {
  const newRepeaterData = [];
  let hasChanges = false;
  let deletedCount = 0;
  let isFirstMatchingItem = true;

  repeaterData.forEach((item, index) => {
    const isMatchingItem = item[symlinkPathPropertyName] === symlinkPropValue;

    if (isMatchingItem) {
      if (isFirstMatchingItem) {
        // Keep only the first matching item
        isFirstMatchingItem = false;
        hasChanges = processAndAddItem(item, newRepeaterData) || hasChanges;
      } else {
        // Remove duplicate items
        dispatch(removeRepeaterItem(repeaterKey, index - deletedCount));
        deletedCount += 1;
        hasChanges = true;
      }
    } else {
      // Keep non-matching items unchanged
      newRepeaterData.push(item);
    }
  });

  return { newRepeaterData, hasChanges };
};

/**
 * Process items when there is no parent key (keep only the first item)
 * @param {Array} repeaterData - repeater data
 * @param {Object} data - complete form data
 * @param {Function} dispatch - Redux dispatch function
 * @param {string} repeaterKey - repeater key
 * @returns {Object} processing result
 */
const processWithoutParentKey = (repeaterData, data, dispatch, repeaterKey) => {
  const newRepeaterData = [];
  let hasChanges = false;
  let deletedCount = 0;
  let childRepeaterInfo = {
    childRepeaterKey: null,
    newChildRepeaterData: [],
    hasChanges: false,
  };

  repeaterData.forEach((item, index) => {
    if (index === 0) {
      // Process only the first item
      hasChanges = processAndAddItem(item, newRepeaterData) || hasChanges;

      // Process child repeaters if they exist
      childRepeaterInfo = processChildRepeaters(item, data);
      hasChanges = hasChanges || childRepeaterInfo.hasChanges;
    } else {
      // Remove all additional items
      dispatch(removeRepeaterItem(repeaterKey, index - deletedCount));
      deletedCount += 1;
      hasChanges = true;
    }
  });

  return { newRepeaterData, hasChanges, childRepeaterInfo };
};

/**
 * Main function that removes duplicate items from a repeater
 * @param {string} repeaterKey - Key of the repeater to process
 * @param {string} parentKey - Key of the parent repeater (optional)
 * @param {number} parentIndex - Index of the parent item (optional)
 * @returns {Function} Redux thunk
 */
const removeRepeater = (repeaterKey, parentKey, parentIndex) => (
  dispatch,
  getState
) => {
  const data = getFormData(getState());
  const repeaterData = data[repeaterKey] || [];

  // Early return si no hay datos que procesar
  if (!Array.isArray(repeaterData) || repeaterData.length === 0) {
    return;
  }

  const { symlinkPropValue } = getParentRepeaterInfo(
    data,
    parentKey,
    parentIndex
  );

  // Process according to whether there is a parent key or not
  const result = parentKey
    ? processWithParentKey(
        repeaterData,
        symlinkPropValue,
        dispatch,
        repeaterKey
      )
    : processWithoutParentKey(repeaterData, data, dispatch, repeaterKey);

  const { newRepeaterData, hasChanges } = result;
  const lengthChanged = newRepeaterData.length !== repeaterData.length;

  // Update data only if there are changes
  if (hasChanges || lengthChanged) {
    const newData = { [repeaterKey]: newRepeaterData };

    // Add child repeater data if it exists and changed
    if (result.childRepeaterInfo?.childRepeaterKey) {
      const {
        childRepeaterKey,
        newChildRepeaterData,
      } = result.childRepeaterInfo;
      const originalChildData = data[childRepeaterKey] || [];

      if (newChildRepeaterData.length !== originalChildData.length) {
        newData[childRepeaterKey] = newChildRepeaterData;
      }
    }

    dispatch(updateGivenDataKeys(newData));
  }
};

export default removeRepeater;
